/**
 * MirrorShareButton — Bouton « Partager le Miroir ».
 *
 * V23.15.61 — Génère une image portrait (1200×1500) prête à publier sur
 * LinkedIn ou X (Twitter). Rendu hors-écran via html2canvas, puis :
 *   • copie dans le presse-papier si l'API le permet
 *   • téléchargement PNG en fallback
 *   • ouvre un menu de partage direct (LinkedIn, X, Copier)
 *
 * L'image contient : header NOAM, date, "Ce matin.", title + body,
 * impact + fenêtre d'action, filigrane Constante Partners.
 *
 * Zéro nouvelle dépendance : html2canvas déjà installé.
 */
import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { Share2, Download, Copy, Linkedin, Twitter, Loader2, X } from "lucide-react";
import { notify } from "@/lib/notify";

const IMPACT_META = {
  eleve: { color: "#E24B4A", label: "élevé" },
  moyen: { color: "#EF9F27", label: "moyen" },
  faible: { color: "#378ADD", label: "faible" },
};

function fmtDayFrLong() {
  return new Date().toLocaleDateString("fr-FR", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
}

export default function MirrorShareButton({ mirror }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  const capture = async () => {
    if (!cardRef.current) throw new Error("Zone de capture introuvable.");
    return html2canvas(cardRef.current, {
      scale: 1.5,
      backgroundColor: "#0A0A0F",
      useCORS: true,
      logging: false,
      width: 1200,
      height: 1500,
    });
  };

  const canvasToBlob = (canvas) => new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));

  const downloadPng = async () => {
    setBusy(true);
    try {
      const canvas = await capture();
      const dataUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `noam-miroir-${new Date().toISOString().slice(0, 10)}.png`;
      a.click();
      notify.success("Image téléchargée · prête à publier.");
    } catch (e) {
      notify.error(e.message || "Capture impossible.");
    } finally {
      setBusy(false);
    }
  };

  const copyToClipboard = async () => {
    setBusy(true);
    try {
      const canvas = await capture();
      const blob = await canvasToBlob(canvas);
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
        notify.success("Image copiée · collez-la dans votre post.");
      } else {
        // fallback : téléchargement
        await downloadPng();
      }
    } catch (e) {
      notify.error(e.message || "Copie impossible.");
    } finally {
      setBusy(false);
    }
  };

  const shareLinkedIn = async () => {
    // LinkedIn ne prend pas d'image via URL — on télécharge et ouvre l'éditeur
    await downloadPng();
    const shareText = encodeURIComponent(
      `${mirror?.title || "News"}\n\nUne observation par jour, générée par Noam — le second cerveau stratégique des dirigeants.\n\n#Stratégie #DécisionIntelligence`
    );
    window.open(`https://www.linkedin.com/feed/?shareActive=true&text=${shareText}`, "_blank", "noopener,noreferrer");
  };

  const shareX = async () => {
    await downloadPng();
    const shareText = encodeURIComponent(
      `${mirror?.title || "News"}\n\nUne observation par jour, générée par Noam — le second cerveau stratégique.\n\n#Stratégie`
    );
    window.open(`https://twitter.com/intent/tweet?text=${shareText}`, "_blank", "noopener,noreferrer");
  };

  if (!mirror || mirror.is_meaningful === false) return null;

  const impactCfg = IMPACT_META[mirror.impact_level] || IMPACT_META.moyen;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-[10px] text-zinc-500 hover:text-white inline-flex items-center gap-1"
        data-testid="miroir-share-btn"
        title="Partager cette observation en image"
      >
        <Share2 className="w-3 h-3" /> Partager le miroir
      </button>

      {/* Card de rendu (hors-écran, capturée par html2canvas) */}
      <div
        style={{
          position: "fixed",
          left: -20000,
          top: 0,
          width: 1200,
          height: 1500,
          pointerEvents: "none",
        }}
        aria-hidden="true"
      >
        <div
          ref={cardRef}
          style={{
            width: 1200,
            height: 1500,
            background: "linear-gradient(135deg, #0A0A0F 0%, #14141C 100%)",
            padding: "80px 80px 60px 80px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            boxSizing: "border-box",
            color: "#FFFFFF",
            fontFamily: '"Space Grotesk", system-ui, sans-serif',
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Halo décoratif */}
          <div
            style={{
              position: "absolute",
              top: -200, right: -200,
              width: 700, height: 700,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${impactCfg.color}25 0%, ${impactCfg.color}00 70%)`,
              pointerEvents: "none",
            }}
          />

          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "relative", zIndex: 2 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 48, height: 48, borderRadius: 10, background: "#EF9F27", display: "flex", alignItems: "center", justifyContent: "center", color: "#0A0A0F", fontWeight: 800, fontSize: 28 }}>N</div>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: -0.5 }}>Noam</div>
                  <div style={{ fontSize: 14, color: "#A0A0AA", marginTop: 2 }}>by Constante Partners</div>
                </div>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, letterSpacing: 4, color: "#EF9F27", fontWeight: 700, textTransform: "uppercase" }}>News</div>
              <div style={{ fontSize: 14, color: "#A0A0AA", marginTop: 6, textTransform: "capitalize" }}>{fmtDayFrLong()}</div>
            </div>
          </div>

          {/* Coeur du message */}
          <div style={{ position: "relative", zIndex: 2 }}>
            <div style={{ fontSize: 13, letterSpacing: 4, color: "#71717A", fontWeight: 700, textTransform: "uppercase", marginBottom: 24 }}>
              Ce matin.
            </div>
            <div style={{
              fontSize: mirror.title && mirror.title.length > 100 ? 44 : 54,
              fontWeight: 700,
              lineHeight: 1.15,
              letterSpacing: -0.8,
              marginBottom: 32,
              color: "#FFFFFF",
            }}>
              {mirror.title}
            </div>
            {mirror.body && (
              <div style={{
                fontSize: 22,
                lineHeight: 1.5,
                color: "#D4D4D8",
                fontWeight: 400,
                maxWidth: 980,
              }}>
                {mirror.body}
              </div>
            )}

            {/* Badges impact + fenêtre */}
            <div style={{ display: "flex", gap: 14, marginTop: 48, flexWrap: "wrap" }}>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "10px 18px", borderRadius: 999,
                background: `${impactCfg.color}18`, border: `1px solid ${impactCfg.color}55`,
                fontSize: 15, fontWeight: 600, color: impactCfg.color,
              }}>
                Impact {impactCfg.label}
              </div>
              {mirror.action_window && (
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "10px 18px", borderRadius: 999,
                  background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)",
                  fontSize: 15, fontWeight: 600, color: "#FFFFFF",
                }}>
                  Fenêtre : {mirror.action_window}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div style={{ position: "relative", zIndex: 2, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 15, color: "#A0A0AA" }}>
              <span style={{ color: "#FFFFFF", fontWeight: 600 }}>Noam</span> — un second cerveau stratégique.
            </div>
            <div style={{ fontSize: 13, color: "#71717A", letterSpacing: 2, textTransform: "uppercase", fontWeight: 600 }}>
              constante-partners.ch
            </div>
          </div>
        </div>
      </div>

      {/* Modal choix de partage */}
      {open && (
        <div
          className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="miroir-share-modal"
        >
          <div
            className="w-full max-w-sm rounded-lg border border-white/10 bg-[#0A0A0F] p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] font-semibold">Partager le miroir</div>
                <div className="text-[15px] text-white font-semibold mt-0.5">Une image, prête à publier</div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1 hover:bg-white/10 rounded"
                data-testid="miroir-share-close"
                disabled={busy}
              >
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>

            <p className="text-[11px] text-zinc-500 leading-relaxed">
              Format 1200×1500 (LinkedIn/X). L&apos;image est générée localement — rien n&apos;est envoyé aux réseaux sociaux avant votre validation.
            </p>

            <div className="space-y-2 pt-2">
              <button
                type="button"
                onClick={shareLinkedIn}
                disabled={busy}
                className="w-full text-left px-3 py-2.5 rounded border border-white/10 hover:border-[#0077B5] hover:bg-[#0077B5]/10 flex items-center gap-3 text-[13px] text-white disabled:opacity-50"
                data-testid="miroir-share-linkedin"
              >
                <Linkedin className="w-4 h-4 text-[#0077B5]" /> Partager sur LinkedIn
                <span className="ml-auto text-[10px] text-zinc-500">(télécharge + ouvre l&apos;éditeur)</span>
              </button>
              <button
                type="button"
                onClick={shareX}
                disabled={busy}
                className="w-full text-left px-3 py-2.5 rounded border border-white/10 hover:border-white/40 hover:bg-white/[0.06] flex items-center gap-3 text-[13px] text-white disabled:opacity-50"
                data-testid="miroir-share-x"
              >
                <Twitter className="w-4 h-4 text-white" /> Partager sur X (Twitter)
                <span className="ml-auto text-[10px] text-zinc-500">(télécharge + ouvre l&apos;éditeur)</span>
              </button>
              <button
                type="button"
                onClick={copyToClipboard}
                disabled={busy}
                className="w-full text-left px-3 py-2.5 rounded border border-white/10 hover:border-[#EF9F27] hover:bg-[#EF9F27]/10 flex items-center gap-3 text-[13px] text-white disabled:opacity-50"
                data-testid="miroir-share-copy"
              >
                <Copy className="w-4 h-4 text-[#EF9F27]" /> Copier l&apos;image
              </button>
              <button
                type="button"
                onClick={downloadPng}
                disabled={busy}
                className="w-full text-left px-3 py-2.5 rounded border border-white/10 hover:border-white/40 hover:bg-white/[0.06] flex items-center gap-3 text-[13px] text-white disabled:opacity-50"
                data-testid="miroir-share-download"
              >
                <Download className="w-4 h-4 text-zinc-400" /> Télécharger (PNG)
              </button>
            </div>

            {busy && (
              <div className="text-[11px] text-zinc-400 inline-flex items-center gap-1.5 pt-1" data-testid="miroir-share-busy">
                <Loader2 className="w-3 h-3 animate-spin" /> Génération de l&apos;image…
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
